/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTheme } from '@material-ui/core';
import type { CSSProperties } from 'react';
import { ScoreSuccessEnum } from '../../api';
import { scoreToColorConverter } from '../../helpers/scoreToColorConverter';
import { percentToScoreSuccess } from '../../helpers/percentToScoreSuccess';

export interface ScoreVerdictProps {
  /** Overall score percentage 0–100, or null/undefined when not measured. */
  scorePercent: number | null | undefined;
  /**
   * Explicit health level. When omitted it is derived from `scorePercent`
   * via {@link percentToScoreSuccess} so the number/rail is always tinted by
   * health rather than a fixed ink colour.
   */
  scoreSuccess?: ScoreSuccessEnum | null;
  /** Optional label used for the accessible name (e.g. "Overall"). */
  label?: string;
}

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

/**
 * The board row's headline verdict: a large, health-tinted number with a small
 * "%" and a slim health-coloured rail beneath it. Number-led by design so the
 * Overall reads as the row's verdict rather than as a fifth {@link ScoreBar}
 * pillar. Health colour comes from the plugin theme's
 * {@link scoreToColorConverter} (via {@link percentToScoreSuccess} when no
 * explicit `scoreSuccess` is given) — never a hard-coded hex. Theme-aware: the
 * rail track uses `theme.palette.divider`, so it reads on light and dark.
 *
 * `scorePercent == null` renders a muted "n/a" defensively — the board guards
 * not-scored Systems with its own "not scored" label, so this is a safety net.
 */
export const ScoreVerdict = ({
  scorePercent,
  scoreSuccess,
  label,
}: ScoreVerdictProps) => {
  const theme = useTheme();
  const measured =
    typeof scorePercent === 'number' && Number.isFinite(scorePercent);
  const success = scoreSuccess ?? percentToScoreSuccess(scorePercent);
  const color = scoreToColorConverter(success ?? undefined);
  const percent = measured ? clampPercent(scorePercent as number) : 0;

  const rootStyle: CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.25rem',
    flexShrink: 0,
  };

  const numberRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.15rem',
    lineHeight: 1,
  };

  const numberStyle: CSSProperties = {
    fontSize: '1.55rem',
    fontWeight: 800,
    color: measured ? color : theme.palette.text.disabled,
    fontVariantNumeric: 'tabular-nums',
  };

  const percentSignStyle: CSSProperties = {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: measured ? color : theme.palette.text.disabled,
  };

  const railStyle: CSSProperties = {
    position: 'relative',
    width: '5.5rem',
    height: '6px',
    borderRadius: '0.25rem',
    backgroundColor: theme.palette.divider,
    overflow: 'hidden',
  };

  const railFillStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${percent}%`,
    backgroundColor: color,
    borderRadius: 'inherit',
    transition: 'width 0.3s ease',
  };

  const accessibleName = measured
    ? `${label ? `${label} ` : ''}score ${percent} percent`
    : `${label ? `${label} ` : ''}not yet measured`;

  return (
    <div
      style={rootStyle}
      data-testid="overall-verdict"
      data-measured={measured ? 'true' : 'false'}
      data-score-success={success ?? 'none'}
      role="img"
      aria-label={accessibleName}
    >
      <span style={numberRowStyle}>
        <span style={numberStyle} data-testid="overall-verdict-number">
          {measured ? percent : 'n/a'}
        </span>
        {measured && <span style={percentSignStyle}>%</span>}
      </span>
      <span style={railStyle} data-testid="overall-verdict-rail">
        {measured && (
          <span style={railFillStyle} data-testid="overall-verdict-rail-fill" />
        )}
      </span>
    </div>
  );
};
